import React, { useState, useEffect } from 'react';
import { buildLongitudeMarkers } from '../utils/longitudeMarkers';
import { buildLongitudeStudyReport } from '../utils/longitudeStudyReport';
import PropTypes from 'prop-types';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, 
  Typography, Box, TextField, RadioGroup, FormControlLabel, Radio, FormControl
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { toYMD } from './DateUtils';
import { logActivity } from '../utils/logActivity';
import { useAuth } from '../context/AuthContext';

const planets = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'];
const COLORS = ['Red', 'Green', 'Blue', 'Yellow', 'Orange', 'Purple', 'Black', 'Gray', 'Brown', 'Pink', 'Cyan', 'White'];

/**
 * How each event is drawn. A vertical line marks the date across the whole pane; dots and arrows
 * sit against the bar itself, which stays readable when events are dense.
 */
/**
 * Bodies with no heliocentric position.
 *
 * The Sun is the origin of that frame. Rahu and Ketu are the lunar nodes — intersections of the
 * Moon's orbit around EARTH with the ecliptic — so they are undefined once Earth is not the
 * centre, and Swiss Ephemeris fails outright rather than returning a poor answer. Requesting one
 * used to surface as an HTTP 500.
 *
 * The Moon IS kept: its heliocentric longitude tracks Earth's, which makes it the usable proxy
 * for Earth in this frame, and Moon-planet pairs are the point of the study.
 */
const NO_HELIOCENTRIC_POSITION = ['Sun', 'Rahu', 'Ketu', 'North Node', 'South Node'];

const DISPLAY_TYPES = [
  { value: 'verticalLine', label: 'Vertical Line' },
  { value: 'dot', label: 'Dot' },
  { value: 'arrow', label: 'Arrow' },
];

// Module scope, not inside the component: it is a constant, and rebuilding it every render made
// it a new object each time, which is why the effect that reads it could not list it as a
// dependency without re-running on every render.
const PLANET2_COLOR_MAP = {
  'Sun': 'Orange',
  'Moon': 'Red',
  'Mercury': 'Gray',
  'Venus': 'Green',
  'Mars': 'Pink',
  'Jupiter': 'Blue',
  'Saturn': 'Brown',
  'Uranus': 'Cyan',
  'Neptune': 'Purple',
  'Pluto': 'Black',
  'Rahu': 'Teal',
};

export default function LongitudeStudyDlg({ open, onClose, onRun, barData, onJournalAppend }) {
  const [displayType, setDisplayType] = useState('verticalLine');
  const { user, api } = useAuth();
  // Common state
  const [color, setColor] = useState(PLANET2_COLOR_MAP['Venus'] || COLORS[0]);
  const [frameOfReference, setFrameOfReference] = useState('Geocentric');
  const [planet1, setPlanet1] = useState('Sun');
  const [planet2, setPlanet2] = useState('Venus');
  const [distance, setDistance] = useState('0');

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");

  // --- Helper for disabling logic ---
  const isRunDisabled = (() => {
    const d = parseInt(distance, 10);
    if (((planet1 === "Sun" && planet2 === "Mercury") || (planet1 === "Mercury" && planet2 === "Sun")) && d > 28) {
      return true;
    }
    if (((planet1 === "Sun" && planet2 === "Venus") || (planet1 === "Venus" && planet2 === "Sun")) && d > 48) {
      return true;
    }
    return false;
  })();

  useEffect(() => {
    if (isRunDisabled) {
      if ((planet1 === "Sun" && planet2 === "Mercury") || (planet1 === "Mercury" && planet2 === "Sun")) {
        setLimitMessage("For Sun and Mercury, the maximum allowed distance is 28 degrees.");
        setShowLimitModal(true);
      } else if ((planet1 === "Sun" && planet2 === "Venus") || (planet1 === "Venus" && planet2 === "Sun")) {
        setLimitMessage("For Sun and Venus, the maximum allowed distance is 48 degrees.");
        setShowLimitModal(true);
      }
    } else {
      setLimitMessage("");
      setShowLimitModal(false);
    }
  }, [isRunDisabled, planet1, planet2, distance]);

  const planetsAreIdentical = planet1 === planet2;

// Auto-update color based on planet2
useEffect(() => {
  if (PLANET2_COLOR_MAP[planet2]) {
    setColor(PLANET2_COLOR_MAP[planet2]);
  }
}, [planet2]);




  useEffect(() => {
    if (frameOfReference === 'Heliocentric') {
      const valid = planets.filter(p => !NO_HELIOCENTRIC_POSITION.includes(p));
      // Reset to Mercury rather than the list head, which is the Sun's old slot and reads as an
      // arbitrary jump; Mercury is the first body that actually has a heliocentric position.
      const fallback = valid.includes('Mercury') ? 'Mercury' : (valid[0] || '');
      if (NO_HELIOCENTRIC_POSITION.includes(planet1)) setPlanet1(fallback);
      if (NO_HELIOCENTRIC_POSITION.includes(planet2)) setPlanet2(fallback);
    }
  }, [frameOfReference, planet1, planet2]);

  const handleRun = async () => {
    const angle = parseInt(distance, 10);

    const eventDetails = {
      studyType: 'longitudinalDistance',
      parameters: {
        planet1,
        planet2,
        degrees: angle,
        frameOfReference,
      }
    };
    await logActivity(api, user, 'RUN_STUDY', eventDetails);
    if (!planet1 || !planet2 || isNaN(angle)) {
      console.error('[LongitudeStudyDlg] Missing required user selections:', { planet1, planet2, angle });
      alert('Please select both planets and enter a valid angle.');
      return;
    }
    if (planet1 === planet2) {
      console.error('[LongitudeStudyDlg] Identical planets selected:', { planet1, planet2 });
      alert('Planets cannot be identical for this study type.');
      return;
    }
    if (!barData || !Array.isArray(barData.dates) || barData.dates.length === 0) {
      console.error('[LongitudeStudyDlg] Chart data check failed:', barData);
      // Only show alert if truly empty, otherwise allow overlay rendering to proceed
      return;
    }
    // Ensure dates are in yyyy-MM-dd format

    // Build query params for getDegreeSeparation
    const params = new URLSearchParams({
      planet1,
      planet2,
      degrees: angle,
      fromDate: toYMD(barData.dates[0]),
      toDate: toYMD(barData.dates[barData.dates.length - 1]),
      // Sent explicitly in both frames rather than only for heliocentric, so the request always
      // states which frame it wants instead of relying on the endpoint's default.
      frame: frameOfReference === 'Heliocentric' ? 'heliocentric' : 'geocentric',
    });
    console.log('[LongitudeStudyDlg] Calling /api/followstudy/get-degree-separation with params:', params.toString());
    try {
      const res = await api.get(`/api/followstudy/get-degree-separation?${params.toString()}`);
      const dates = res.data;
      console.log('[LongitudeStudyDlg] Parsed dates:', dates);

      // The statistics summary, logged to the Journal tab.
      //
      // Derived here rather than requested from the backend: the endpoint returns dates only, and
      // every bar the figures are computed from is already loaded in the browser. Failing to build
      // the report must not cost the customer their overlays, so it is guarded separately from the
      // drawing below.
      if (typeof onJournalAppend === 'function') {
        try {
          const { report } = buildLongitudeStudyReport({
            planet1,
            planet2,
            distance: angle,
            frame: frameOfReference,
            eventDates: dates,
            barData,
          });
          onJournalAppend({
            studyLabel: `${planet1} - ${angle}° - ${planet2} (${frameOfReference})`,
            reportText: report,
          });
        } catch (reportErr) {
          console.error('[LongitudeStudyDlg] Could not build the journal report:', reportErr);
        }
      }

      // Pass overlays up via onRun or handle locally
      if (onRun) {
        // Construct a valid study object for App.js
        const overlays = displayType === 'verticalLine'
          ? dates.map(date => ({
              type: 'verticalLine',
              planet1,
              planet2,
              degrees: angle,
              displayType,
              color,
              thickness: 2, // match overlays
              frameOfReference: frameOfReference || '',
              label: `${planet1}-${angle}-${planet2}\n${date.split('T')[0]}`,
              date: date.split('T')[0]
            }))
          // Dots and arrows anchor to the bar, so they need its high/low. One scatter overlay
          // carries every event rather than one per date: a hundred single-point series would
          // bloat the option object and make the legend meaningless.
          : [buildLongitudeMarkers(dates, barData, {
              displayType,
              color,
              label: `${planet1}-${angle}-${planet2}`,
            })].filter(Boolean);
        onRun(overlays);
      }
    } catch (err) {
      alert(err.message);
    }
    // Back to geocentric for the next study. The frame lives in this dialog's state, so without
    // this it persisted across runs and a later study silently inherited heliocentric from an
    // earlier one — the leak reported, though it is dialog state rather than anything global:
    // the backend takes the frame per request and holds none between them.
    setFrameOfReference('Geocentric');
    onClose();
  };

  const handleDistanceChange = (event) => {
    let value = event.target.value;
    if (value === '') {
      setDistance('');
      return;
    }
    let numValue = parseInt(value, 10);
    if (isNaN(numValue)) numValue = 0;
    if (numValue > 359) numValue = 359;
    if (numValue < 0) numValue = 0;
    setDistance(numValue.toString());
  };

  const currentPlanetList = frameOfReference === 'Heliocentric'
    ? planets.filter(p => !NO_HELIOCENTRIC_POSITION.includes(p))
    : planets;

  const renderLongitudinalDistanceFields = () => (
    <>
      <Box display="flex" flexDirection="row" justifyContent="space-between" width="100%" gap={2}>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Planet 1</Typography>
          <Select value={planet1} onChange={(e) => setPlanet1(e.target.value)} error={planetsAreIdentical}>
            {currentPlanetList.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Planet 2</Typography>
          <Select value={planet2} onChange={(e) => setPlanet2(e.target.value)} error={planetsAreIdentical}>
            {currentPlanetList.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <FormControl fullWidth>
        <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Distance (in degrees)</Typography>
        <TextField size="small" value={distance} onChange={handleDistanceChange} />
      </FormControl>
    </>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ style: { width: '560px', borderRadius: '12px' } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem', borderBottom: '1px solid #eee', pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Longitude Study
          <HelpIcon helpKey="longitudeStudy" />
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5} width="100%">
            {renderLongitudinalDistanceFields()}

          {/* Common Fields */}
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
            <Box display="flex" flexDirection="column" width="50%">
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Display type</Typography>
              <Select value={displayType} onChange={e => setDisplayType(e.target.value)} size="small" fullWidth>
                {DISPLAY_TYPES.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </Select>
            </Box>
            <FormControl fullWidth size="small" style={{width: '50%'}}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Color</Typography>
              <Select value={color} onChange={(e) => setColor(e.target.value)} renderValue={(selected) => (
  <Box display="flex" alignItems="center">
    <span style={{
      display: 'inline-block',
      width: 18,
      height: 18,
      borderRadius: '50%',
      backgroundColor: selected.toLowerCase(),
      marginRight: 8,
      border: '1px solid #bbb'
    }} />
    {selected}
  </Box>
)}>
  {COLORS.map((c) => (
    <MenuItem key={c} value={c}>
      <Box display="flex" alignItems="center">
        <span style={{
          display: 'inline-block',
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: c.toLowerCase(),
          marginRight: 8,
          border: '1px solid #bbb'
        }} />
        {c}
      </Box>
    </MenuItem>
  ))}
</Select>
            </FormControl>
          </Box>
          
          <FormControl component="fieldset">
            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.5 }}>Frame of Reference</Typography>
            <RadioGroup row value={frameOfReference} onChange={(e) => setFrameOfReference(e.target.value)}>
              <FormControlLabel value="Geocentric" control={<Radio />} label="Geocentric" />
              <FormControlLabel value="Heliocentric" control={<Radio />} label="Heliocentric" />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', borderTop: '1px solid #eee', justifyContent: 'space-between' }}>
        <Button onClick={onClose} color="secondary" variant="outlined">CLOSE</Button>
        <Box>
          <Button
            onClick={() => {
              if (isRunDisabled) setShowLimitModal(true);
              else handleRun();
            }}
            variant="contained"
            disabled={isRunDisabled || planetsAreIdentical}
          >
            Run
          </Button>
        </Box>
        {/* Modal for limit violations */}
        <Dialog open={showLimitModal} onClose={() => setShowLimitModal(false)}>
          <DialogTitle>Invalid Distance for Selected Pair</DialogTitle>
          <DialogContent>
            <Typography>{limitMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLimitModal(false)} color="primary">OK</Button>
          </DialogActions>
        </Dialog>
      </DialogActions>
    </Dialog>

    </>
  );
}

LongitudeStudyDlg.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    symbol: PropTypes.string,
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.number)
    ),
    volumes: PropTypes.arrayOf(PropTypes.number),
  }),
  onAddToCSG: PropTypes.func, // Optional, but needed for CSG integration
  /** Appends the run's statistics report to the Message Panel's Journal tab. */
  onJournalAppend: PropTypes.func,
};
